<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { debouncer } from '@/util/debouncer'
import { ALGOLIA_BYTE_LIMIT, isQueryTooLong } from '@/util/browse'

const props = withDefaults(
  defineProps<{
    initialQuery?: string
    labelText?: string
    hideLabel?: boolean
    placeholderText?: string
    buttonText?: string
    debounceDurationMs?: number
    hideButton?: boolean
    inputId?: string
    byteLimitEnforced?: boolean
    byteLimit?: number
    byteLimitMessage?: string
  }>(),
  {
    initialQuery: '',
    labelText: 'Search',
    hideLabel: false,
    placeholderText: 'Keyword search',
    buttonText: 'Search',
    debounceDurationMs: 200,
    hideButton: false,
    inputId: 'search-input',
    byteLimitEnforced: false,
    byteLimit: ALGOLIA_BYTE_LIMIT,
    byteLimitMessage: 'Please use a shorter search query.'
  }
)

const emit = defineEmits<{ 'query:changed': [query: string] }>()

const searchQuery = ref(props.initialQuery)

const warningId = computed(() => `${props.inputId}-warning`)
const byteLimitReached = computed(
  () => props.byteLimitEnforced && isQueryTooLong(searchQuery.value, props.byteLimit)
)

const emitQuery = debouncer(() => emit('query:changed', searchQuery.value), props.debounceDurationMs)

function submitQuery() {
  emitQuery.cancel()
  emit('query:changed', searchQuery.value)
}

watch(
  () => props.initialQuery,
  (initialQuery) => {
    // the keyword can be cleared outside this component, which has to clear the input too
    if (searchQuery.value && !initialQuery) {
      searchQuery.value = ''
    }
  }
)
</script>

<template>
  <div>
    <label :for="inputId" class="mb-1 block text-text-sm font-bold text-gray-800" :class="{ 'sr-only': hideLabel }">
      {{ labelText }}
    </label>
    <div class="flex">
      <input
        :id="inputId"
        v-model="searchQuery"
        type="search"
        class="w-full rounded-xs border border-gray-400 px-3 py-1.5 text-text-lg text-gray-900 focus:outline-offset-2"
        :class="{ 'rounded-r-none': !hideButton }"
        :placeholder="placeholderText"
        :aria-describedby="byteLimitReached ? warningId : undefined"
        :aria-invalid="byteLimitReached"
        @input="emitQuery()"
      />
      <slot v-if="!hideButton" name="button">
        <button
          type="button"
          class="rounded-xs rounded-l-none bg-blue-400 px-3 py-1.5 text-text-lg text-white disabled:opacity-50"
          :disabled="!searchQuery"
          @click="submitQuery()"
        >
          <span class="material-icons-round align-middle text-text-lg" aria-hidden="true">search</span>
          <span class="sr-only sm:not-sr-only sm:ml-1">{{ buttonText }}</span>
        </button>
      </slot>
    </div>
    <p v-if="byteLimitReached" :id="warningId" role="status" class="mt-1 text-center text-text-md text-gray-900">
      {{ byteLimitMessage }}
    </p>
  </div>
</template>
